/**
 * Which of a forward cockpit view's draw ops are the **aeroplane** and which are
 * the **dashboard** — the one classification 3D COCKPIT needs
 * (`docs/engineering/cockpit-3d-design.md` §1).
 *
 * ## The split is the original's own, not a shape heuristic
 *
 * The design note proposed classifying by op kind and geometry: sprites and
 * gauges are dashboard, vector ops are airframe unless they sit wholly inside
 * the dash band (`dash.originY`, 98 on a Camel). Run against the real Camel
 * composition that rule is **wrong on 5 of the 37 forward ops**: four of the
 * cowling-shading polygons (idle ops 0-3) and one of the cabane struts' feet (a
 * degenerate line at (61,108), op 18) lie at y >= 98 and would have been kept as
 * panel furniture though they are the aeroplane.
 * `tools/bake/test/cockpit-airframe.test.ts` runs both rules and pins the
 * disagreement, so the note's version cannot come back by looking reasonable.
 * (It was wrong on eleven until 2026-08-26; the six that have since changed
 * sides are §"The gun blocks also touch up the cowling" below, where the band
 * rule turns out to have been right about the answer and wrong about the
 * reason.)
 *
 * The discriminator that is not a guess is the script's own block structure,
 * which `packages/baker/src/cockpit-bake.ts` disassembled for all 22 aircraft
 * and states in its module table:
 *
 * | block | content |
 * |---|---|
 * | 1 | the dash sheet twice, the gun/sight bases, **and the cowling-shading polys** |
 * | 2 | **the airframe around the panel**: wing struts, rigging cables, centre fitting |
 * | 3, 4 | the left and right guns' bodies (5, 6 the same two firing) |
 *
 * So the rule starts: **airframe = every op block 2 emits, plus every non-sprite
 * op block 1 emits.** Inside block 1 the sprites are the dash/gun/sight bases by
 * the table's own words and the vector ops are the cowling shading, so no
 * position is consulted anywhere. On CAMEL that gives 21 airframe ops out of 37
 * idle and 21 out of 33 firing — the same 21 both times, which is what it should
 * be, because blocks 1 and 2 are shared by both variants and only the gun blocks
 * differ. `tools/bake/test/cockpit-airframe.test.ts` re-derives the whole table from the real
 * `.TTM` scripts, so this file cannot drift from what the blocks actually say.
 *
 * ## The gun blocks also touch up the cowling, and the colour says so
 *
 * User report, 2026-08-26, flying the Camel in 3D COCKPIT: *"next to the right
 * gun there are artifacts of the painted plane front (yellow) that shouldn't be
 * there."* They are ops 29-33 and 35 — four two-to-six-pixel lines and two lone
 * pixels at x 200-210, y 92-94, all in colour **19**. Block 4 emits them, so the
 * block rule above kept every one of them as gun art, and the earlier revision
 * of this file called them "detail on the right gun's body".
 *
 * They are not. Colour 19 is `BARON.PAL` `#aa8a38`, and on the Camel it is used
 * by exactly one other thing in the whole forward view: block 1's cowling-shading
 * polygons 4 and 5, the painted nose the model replaces. Those marks are the
 * cowl's own paint, repairing its silhouette where it meets the gun base sprite
 * at (210,94) — so once the cowl is gone they are six yellow specks of nothing,
 * half a metre from the pilot's eye, beside the right gun. Exactly the percept.
 *
 * Three readings off the real scripts make the identification, not the reasoning:
 *
 * 1. **They are boilerplate, shared between aeroplanes whose guns differ.**
 *    CAMEL, SOPPUP, FOKD7, SNIPE and SPAD_7 all emit the identical cluster —
 *    `line (203,94)-(208,94)`, `line (207,93)-(202,93)`, `pixel (200,92)` — in
 *    the identical colour, from their own gun blocks. A Camel's twin Vickers, a
 *    D.VII's twin Spandau and a SPAD 7's single Vickers are different guns and
 *    would not share a gun *detail* to the pixel. What they share is the DASH_B
 *    panel layout, which puts the gun base at the same screen place against the
 *    same painted nose. SOPTRI and SPAD_13 carry variants of the same repair
 *    (SPAD_13 has a left-hand pair as well, x 114-123); NIEU11 and NIEUPORT a
 *    four-pixel one in colour 21.
 * 2. **They exist only in the idle variant.** On all 22 scouts, no gun-block op
 *    of a cowling colour appears in the firing view — because the firing frames
 *    cover that notch themselves. Gun-body detail would still be there with the
 *    trigger down; a patch over a seam would not.
 * 3. **Every one of them is tiny**: the largest is a nine-pixel line. No
 *    aeroplane loses a piece of gun to this.
 *
 * So the rule gains a second clause, and it consults paint rather than position
 * — the discriminator the design note's rejected band rule got wrong by reaching
 * for coordinates: **a non-sprite op in a gun block is airframe when its colour
 * is one that block 1's own non-sprite ops use and block 2's do not.** In one
 * sentence: *drop with the paint you already dropped.* The colours are read out
 * of the very op list being classified — nothing new is tabled — and block 2's
 * colours are excluded so a grey rigging tone shared with a gun mounting cannot
 * drag gun art out of the painting (it excuses SOPTRI's colour-72 line and both
 * SPADs' colour-72 gun rigging, which stay painted).
 *
 * **The guns-and-sight invariant is untouched and is why this is safe.** The
 * clause only ever looks at non-sprite ops, and every piece of gun, sight and
 * muzzle-flash art in every script is a **sprite blit**. Nothing drawn from
 * `LT_GUN_B`, `RT_GUN_B`, `GUN_B` or `SIGHT` can be reclassified by any colour.
 *
 * ## Why the spans are a checked-in table
 *
 * Because the baked composition is a *flat* op list with no block boundaries in
 * it, and a pure function here must not read `data/` at import time — the same
 * arrangement, for the same reason, as `apps/web/src/flight/cockpit-view.ts`'s
 * `EYE_OFFSETS`. Two
 * integers per aeroplane is the whole of it, and the op *kinds* they are applied
 * to are read live out of the composition the panel already loaded.
 */

import type { CockpitDrawOp } from "./cockpit-types.ts";

/**
 * How many forward-view ops blocks 1 and 2 emit, per aircraft.
 *
 * One entry, not two: blocks 1 and 2 are in **both** the idle variant (1+2+3+4)
 * and the firing one (1+2+5+6), they run from the same interpreter state, and
 * they run first — so the same two counts index the same two prefixes of either
 * op list. Measured on all 22 flyable scouts by running the real scripts block
 * by block; the test does it again.
 */
export interface ForwardBlockSpans {
  /** Block 1: the dash/gun/sight sprite bases *and* the cowling-shading polygons. */
  readonly block1: number;
  /** Block 2: the airframe around the panel — struts, rigging, centre fitting. All of it, whatever the kinds. */
  readonly block2: number;
}

/**
 * Keyed by `AircraftKey`. All 22 flyable scouts are listed because the numbers
 * are mechanical and the census cost nothing; what is **not** fleet-wide is the
 * judgement that block 1's vector ops are cowling rather than panel furniture,
 * which has had its human review pass on the Camel alone — hence the app's own
 * `COCKPIT_3D_AIRCRAFT`.
 */
export const FORWARD_BLOCK_SPANS: Readonly<Record<string, ForwardBlockSpans>> = {
  MORANE: { block1: 2, block2: 17 },
  FOKE3: { block1: 18, block2: 14 },
  AIRCO: { block1: 2, block2: 10 },
  NIEU11: { block1: 28, block2: 20 },
  NIEUPORT: { block1: 28, block2: 20 },
  SOPPUP: { block1: 15, block2: 4 },
  SPAD_7: { block1: 26, block2: 6 },
  ALBA_D2: { block1: 29, block2: 4 },
  SOPTRI: { block1: 28, block2: 12 },
  ALBA_D3: { block1: 29, block2: 4 },
  HALBD2: { block1: 15, block2: 7 },
  SE5: { block1: 28, block2: 8 },
  ALBA_D5: { block1: 29, block2: 4 },
  CAMEL: { block1: 16, block2: 10 },
  PFALZ: { block1: 29, block2: 4 },
  FOKDR1: { block1: 29, block2: 6 },
  SPAD_13: { block1: 27, block2: 19 },
  NIEU28: { block1: 29, block2: 10 },
  FOKD7: { block1: 16, block2: 7 },
  SIEMENS: { block1: 29, block2: 6 },
  SNIPE: { block1: 16, block2: 10 },
  FOKD8: { block1: 29, block2: 11 },
};

/** A non-sprite op's palette index; `undefined` for a sprite blit, which has no colour of its own. */
function opColor(op: CockpitDrawOp | undefined): number | undefined {
  return op && op.kind !== "sprite" ? op.color : undefined;
}

/**
 * The palette indices block 1's cowling ops paint with and block 2's structure
 * ops never do — the "drop with the paint you already dropped" set of the module
 * doc's second clause.
 *
 * Block 2 is subtracted rather than unioned in because a tone the struts and the
 * rigging share with a gun's own mounting (colour 72 on SOPTRI and both SPADs)
 * would otherwise pull gun art off the painting. The cowling's ramp is the only
 * paint this clause is entitled to reclaim.
 */
function cowlOnlyColors(ops: readonly CockpitDrawOp[], blockOneEnd: number, blockTwoEnd: number): Set<number> {
  const colors = new Set<number>();
  for (let index = 0; index < blockOneEnd; index++) {
    const color = opColor(ops[index]);
    if (color !== undefined) colors.add(color);
  }
  for (let index = blockOneEnd; index < blockTwoEnd; index++) {
    const color = opColor(ops[index]);
    if (color !== undefined) colors.delete(color);
  }
  return colors;
}

/**
 * The op indices of one forward view that the aeroplane's own structure draws —
 * block 2 entire, block 1's non-sprite ops, and the gun blocks' non-sprite ops
 * that paint in a cowling-only colour. See the module doc for all three clauses
 * and for the user report that produced the third.
 *
 * An aircraft with no entry in `FORWARD_BLOCK_SPANS` classifies **nothing** as
 * airframe, which is the safe direction: the panel then draws its whole
 * composition exactly as it always did.
 */
export function airframeOpIndices(ops: readonly CockpitDrawOp[], spans: ForwardBlockSpans | undefined): number[] {
  if (!spans) return [];
  const indices: number[] = [];
  const blockOneEnd = Math.min(spans.block1, ops.length);
  for (let index = 0; index < blockOneEnd; index++) {
    if (ops[index]?.kind !== "sprite") indices.push(index);
  }
  const blockTwoEnd = Math.min(spans.block1 + spans.block2, ops.length);
  for (let index = blockOneEnd; index < blockTwoEnd; index++) indices.push(index);
  const cowling = cowlOnlyColors(ops, blockOneEnd, blockTwoEnd);
  for (let index = blockTwoEnd; index < ops.length; index++) {
    const color = opColor(ops[index]);
    if (color !== undefined && cowling.has(color)) indices.push(index);
  }
  return indices;
}

/**
 * The same forward view with the airframe ops taken out — what the panel paints
 * under 3D COCKPIT, and therefore what the dash quad is textured from.
 *
 * Order is preserved, which matters: these are painter's-algorithm ops and the
 * gun-body detail lines are drawn over the sprite beneath them.
 */
export function dashFamilyOps(ops: readonly CockpitDrawOp[], aircraftKey: string | undefined): readonly CockpitDrawOp[] {
  const spans = aircraftKey ? FORWARD_BLOCK_SPANS[aircraftKey.toUpperCase()] : undefined;
  if (!spans) return ops;
  const dropped = new Set(airframeOpIndices(ops, spans));
  return ops.filter((_, index) => !dropped.has(index));
}
